import { useState } from "react";

import { refusalText } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import type { ElementRow, LineRow } from "../../../api/types";
import { useCurrentMe } from "../../../app/auth";
import { Button, ConfirmDialog, Dialog } from "../../../ui";
import { useCommandId, useRefresh } from "../hooks";
import { describeLine } from "../lib/lines";
import { hasChoices, lineChoices } from "../lib/lineRules";
import { FormError, TextArea } from "./fields";

/** What a change to one line makes stale. The live channel does the same for everyone; this is for the person's own press. */
const AFTER_LINE_CHANGE = ["element", "item_page", "item_snapshot", "inbox", "sme_card", "symbol_set", "session_cards", "back_lane"] as const;

const REMOVAL_REASONS = ["No deliverable needs it any more", "It is wrong or a duplicate", "Another line replaces it", "Out of scope"];

type Which = "take_out" | "mark" | "lift" | "drop" | null;

interface LineActionsProps {
  line: LineRow;
  /** The element the line hangs on: whether it is validated decides which step the line offers. */
  element: Pick<ElementRow, "state" | "origin">;
}

/**
 * The four steps an engineer can take on one line (CP-12), one line at a time: take it out (it was never validated), mark
 * it for removal (it is validated), take the mark off, and drop the Next version the person made. There is no step for
 * many lines at once. Only an engineer sees them; the server checks every press again and a refusal is shown in its words.
 */
export function LineActions({ line, element }: LineActionsProps) {
  const me = useCurrentMe();
  const [open, setOpen] = useState<Which>(null);
  const engineer = me.active?.roles.includes("engineer") ?? false;
  if (!engineer) return null;
  const choices = lineChoices(line, element, me.active?.membership_id);
  if (!hasChoices(choices)) return null;
  const title = describeLine(line).title;
  const close = () => setOpen(null);

  return (
    <div className="line-actions" role="group" aria-label={`What you can do with this line: ${title}`}>
      {choices.takeOut && (
        <Button variant="quiet" onClick={() => setOpen("take_out")} aria-label={`Take out: ${title}`}>
          Take out
        </Button>
      )}
      {choices.markRemoval && (
        <Button variant="quiet" onClick={() => setOpen("mark")} aria-label={`Mark for removal: ${title}`}>
          Mark for removal
        </Button>
      )}
      {choices.liftRemoval && (
        <Button variant="quiet" onClick={() => setOpen("lift")} aria-label={`Take the mark off: ${title}`}>
          Take the mark off
        </Button>
      )}
      {choices.dropMine && (
        <Button variant="quiet" onClick={() => setOpen("drop")} aria-label={`Drop my change: ${title}`}>
          Drop my change
        </Button>
      )}
      {choices.othersChange !== null && (
        <p className="line-actions__note muted" role="note">
          {choices.othersChange || "A colleague"} has a pending change on this line. Wait until the SME has settled it, then make yours.
        </p>
      )}
      {choices.othersRemoval && (
        <p className="line-actions__note muted" role="note">
          A colleague marked this line for removal. Only that colleague can take the mark off.
        </p>
      )}

      {open === "take_out" && <TakeOutDialog line={line} title={title} onClose={close} />}
      {open === "mark" && <MarkDialog line={line} title={title} onClose={close} />}
      {open === "lift" && <LiftDialog line={line} title={title} onClose={close} />}
      {open === "drop" && <DropDialog line={line} title={title} onClose={close} />}
    </div>
  );
}

function TakeOutDialog({ line, title, onClose }: { line: LineRow; title: string; onClose: () => void }) {
  const take = useApiCommand("withdraw_line");
  const ids = useCommandId();
  const refresh = useRefresh();
  return (
    <ConfirmDialog
      open
      title="Take this line out?"
      message={`${title}. It was never validated, so nobody needs to decide. It leaves the files at once.`}
      confirmLabel="Take it out"
      cancelLabel="No, leave it"
      busy={take.isPending}
      error={take.error ? refusalText(take.error) : null}
      onCancel={onClose}
      onConfirm={() =>
        take.mutate(
          { id: line.id, expected_version: line.version, command_id: ids.current() },
          {
            onSuccess: () => {
              ids.renew();
              refresh(...AFTER_LINE_CHANGE);
              onClose();
            },
          },
        )
      }
    />
  );
}

function MarkDialog({ line, title, onClose }: { line: LineRow; title: string; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const mark = useApiCommand("mark_line_removal");
  const ids = useCommandId();
  const refresh = useRefresh();
  const submit = () => {
    if (!reason.trim()) {
      setProblem("Say why it should be removed.");
      return;
    }
    setProblem(null);
    mark.mutate(
      { id: line.id, expected_version: line.version, reason: reason.trim(), command_id: ids.current() },
      {
        onSuccess: () => {
          ids.renew();
          refresh(...AFTER_LINE_CHANGE);
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      closeOnBackdrop={false}
      open
      onClose={onClose}
      title="Mark this line for removal"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={mark.isPending} onClick={submit}>
            Mark it for removal
          </Button>
        </>
      }
    >
      <p>
        {title}. It stays in the files until the SME validates the removal. You can take the mark off before that.
      </p>
      <div className="row quick-reasons" role="group" aria-label="Usual reasons">
        {REMOVAL_REASONS.map((text) => (
          <Button key={text} variant="quiet" onClick={() => setReason(text)}>
            {text}
          </Button>
        ))}
      </div>
      <TextArea label="Why should it be removed?" required value={reason} onChange={setReason} error={problem} />
      <FormError message={mark.error ? refusalText(mark.error) : null} />
    </Dialog>
  );
}

function LiftDialog({ line, title, onClose }: { line: LineRow; title: string; onClose: () => void }) {
  const lift = useApiCommand("lift_line_removal");
  const ids = useCommandId();
  const refresh = useRefresh();
  return (
    <ConfirmDialog
      open
      title="Take the removal mark off?"
      message={`${title}. The line stays as it is.`}
      confirmLabel="Take it off"
      busy={lift.isPending}
      error={lift.error ? refusalText(lift.error) : null}
      onCancel={onClose}
      onConfirm={() =>
        lift.mutate(
          { id: line.id, expected_version: line.version, command_id: ids.current() },
          {
            onSuccess: () => {
              ids.renew();
              refresh(...AFTER_LINE_CHANGE);
              onClose();
            },
          },
        )
      }
    />
  );
}

function DropDialog({ line, title, onClose }: { line: LineRow; title: string; onClose: () => void }) {
  const drop = useApiCommand("drop_line_next_version");
  const ids = useCommandId();
  const refresh = useRefresh();
  return (
    <ConfirmDialog
      open
      title="Drop your change on this line?"
      message={`${title}. What the SME validated stays official. The change you made to this line is thrown away.`}
      confirmLabel="Drop it"
      busy={drop.isPending}
      error={drop.error ? refusalText(drop.error) : null}
      onCancel={onClose}
      onConfirm={() =>
        drop.mutate(
          { id: line.id, expected_version: line.version, command_id: ids.current() },
          {
            onSuccess: () => {
              ids.renew();
              refresh(...AFTER_LINE_CHANGE);
              onClose();
            },
          },
        )
      }
    />
  );
}
